import type { RemontaDossieSinistro } from "@shared/schema";
import { DOSSIER_FIELD_LABELS, type DossierFormState } from "./dossie-form";

type FieldKey = Exclude<keyof DossierFormState, "objetoTipo">;

const GROUPS: { title: string; fields: FieldKey[]; wide?: FieldKey[] }[] = [
  { title: "Cliente e solicitação", fields: ["clienteEntidade", "entidadeTipo", "entidadeCnpj", "unidadeFilial", "profissionalSolicitante", "solicitanteSetor", "solicitanteEmail", "solicitanteTelefone", "solicitadoEm", "prazoSolicitado"] },
  { title: "Sinistro e cobertura", fields: ["numeroSinistro", "numeroApolice", "seguradoNome", "seguradoDocumento", "vigenciaInicio", "vigenciaFim", "coberturaAcionada", "franquiaParticipacao", "regulacaoStatus", "valorReclamado"] },
  { title: "Dados do evento", fields: ["dataSinistro", "eventoHorario", "eventoMunicipio", "eventoUf", "eventoEndereco", "eventoLocalizacao", "eventoOrgaoRegistro", "eventoNaturezas", "descricaoEvento"], wide: ["descricaoEvento"] },
  { title: "Veículos, bens e terceiros", fields: ["veiculosEnvolvidos", "bensTerceirosEnvolvidos"], wide: ["veiculosEnvolvidos", "bensTerceirosEnvolvidos"] },
  { title: "Contatos e documentos", fields: ["contatoAcessoNome", "contatoAcessoTelefone", "canalEntrega", "documentosIniciaisReferencia", "responsavelInformacoesNome", "responsavelInformacoesCargo", "autorizadoEnvio"] },
];
const MONEY: FieldKey[] = ["franquiaParticipacao", "valorReclamado"];

function display(field: FieldKey, value: unknown): string {
  if (value === null || value === undefined || value === "") return "";
  if (field === "autorizadoEnvio") return value ? "Confirmada" : "";
  if (Array.isArray(value)) return value.join(", ");
  if (MONEY.includes(field)) return Number(value).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)) return new Date(`${value}T12:00:00`).toLocaleDateString("pt-BR");
  return String(value);
}

/** Dados F01 de abertura gravados no Dossiê (somente leitura). */
export function DossieEnvolvidos({ dossier }: { dossier: RemontaDossieSinistro }) {
  const record = dossier as unknown as Record<string, unknown>;
  const hasAny = GROUPS.some((group) => group.fields.some((field) => field !== "dataSinistro" && display(field, record[field])));
  if (!hasAny) {
    return <p className="text-sm text-muted-foreground">Este Dossiê foi aberto sem o formulário de abertura; não há dados de cliente, evento ou envolvidos registrados.</p>;
  }
  return (
    <div className="space-y-6">
      {GROUPS.map((group) => (
        <section key={group.title} aria-labelledby={`envolvidos-${group.fields[0]}`} className="min-w-0">
          <h3 id={`envolvidos-${group.fields[0]}`} className="mb-2 border-b pb-1.5 text-sm font-semibold text-slate-900">{group.title}</h3>
          <dl className="grid grid-cols-1 gap-x-6 gap-y-2 text-sm sm:grid-cols-2">
            {group.fields.map((field) => {
              const value = display(field, record[field]);
              return (
                <div key={field} className={`min-w-0 ${group.wide?.includes(field) ? "sm:col-span-2" : ""}`}>
                  <dt className="text-xs text-muted-foreground">{DOSSIER_FIELD_LABELS[field]}</dt>
                  <dd className={`whitespace-pre-line [overflow-wrap:anywhere] ${value ? "text-slate-900" : "text-slate-500"}`}>{value || "Não informado"}</dd>
                </div>
              );
            })}
          </dl>
        </section>
      ))}
    </div>
  );
}
